import type { QueryClient } from '@tanstack/react-query';
import type { Project } from '@vd/types';

/** When, after a change of URL patterns, the project is read again for the icon the worker's look found. */
const LOOK_READS_MS = [3000, 8000, 15000];

/**
 * Reads a project (and its workspace's project list) again a few times over
 * the next fifteen seconds (16 Oct): a change of URL patterns queues a look
 * for the site's icon in the worker, which finishes after the save has
 * answered, so without this the new icon showed only after a reload.
 */
export function readIconSoon(queryClient: QueryClient, projectId: string, companyId: string | undefined): void {
  for (const wait of LOOK_READS_MS) {
    setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      if (companyId) void queryClient.invalidateQueries({ queryKey: ['projects', companyId] });
    }, wait);
  }
}

/** A project's icon fields, as its answers carry them. */
type IconFields = Pick<Project, 'iconUrl' | 'iconChoice' | 'hasUploadedIcon' | 'iconCheckedAt' | 'iconSite'>;

/**
 * Puts a project's new icon into the workspace's project list at once, so
 * the sidebar and every other mark show it with the settings field rather
 * than at the list's next read, then reads the list again to be sure (16 Oct).
 * The field read the project and the list together; a look that finished
 * between the two left the list holding the old icon after the field had
 * stopped reading, so the sidebar kept the old picture until a reload.
 */
export function showIconInList(queryClient: QueryClient, companyId: string, projectId: string, fields: Partial<IconFields>): void {
  const known = Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined)) as Partial<IconFields>;
  queryClient.setQueryData<Project[]>(['projects', companyId], (list) => list?.map((project) => (project.id === projectId ? { ...project, ...known } : project)));
  void queryClient.invalidateQueries({ queryKey: ['projects', companyId] });
}
